import React, { useState, Component } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { useInvoiceRequests, getOrderItemsSnapshot, markInvoiceAsSent, InvoiceRequestOrder } from '../../lib/invoice-requests-hooks';
import { useBillingInfo, processPayment, openPrintableInvoice, PaymentRecord } from '../../lib/billing-hooks';
import { cn, formatCurrency } from '../../lib/utils';
import { FileText, Mail, Printer, CheckCircle2, AlertCircle, Loader2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/Button';
export function InvoiceRequestsPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    pendingRequests,
    sentRequests,
    loading: requestsLoading,
    refetch
  } = useInvoiceRequests(restaurant?.id);
  const {
    info: billingInfo,
    loading: infoLoading
  } = useBillingInfo(restaurant?.id);
  const [activeTab, setActiveTab] = useState<'pending' | 'sent'>('pending');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({
      type,
      message
    });
    setTimeout(() => setToast(null), 3500);
  };
  const handleSendInvoice = async (order: InvoiceRequestOrder) => {
    if (!restaurant?.id || !billingInfo) {
      showToast('error', 'Falta información de facturación del restaurante.');
      return;
    }
    setProcessingId(order.id);
    try {
      // 1. Get items snapshot
      const itemsSnapshot = await getOrderItemsSnapshot(order.id);
      if (itemsSnapshot.length === 0) {
        throw new Error('No se encontraron ítems para esta orden.');
      }
      // 2. Process payment (create payment record)
      // We assume it's fully paid via card for marketplace orders
      const paymentResult = await processPayment({
        restaurantId: restaurant.id,
        orderId: order.id,
        tableId: null,
        amount: order.total,
        method: 'card',
        items: itemsSnapshot,
        isPartial: false,
        createdBy: tenantCtx?.userId || null
      });
      if (!paymentResult.success || !paymentResult.payment) {
        throw new Error(paymentResult.error || 'Error al crear el registro de pago.');
      }
      const payment = paymentResult.payment;
      // 3. Mark as sent in DB
      const markResult = await markInvoiceAsSent(order.id, payment.id);
      if (!markResult.success) {
        throw new Error(markResult.error || 'Error al actualizar la orden.');
      }
      // 4. Open email client
      const consecStr = String(payment.consecutive_number).padStart(6, '0');
      const invoiceUrl = `${window.location.origin}/factura/${payment.id}`;
      const greeting = order.invoice_customer_name?.trim() || 'Cliente';
      const emailBody = `Estimad@ ${greeting},\n\n` + `Agradecemos profundamente su preferencia.\n\n` + (order.invoice_legal_id?.trim() ? `Datos del cliente:\n` + `Nombre: ${greeting}\n` + `Cédula / Identificación: ${order.invoice_legal_id.trim()}\n\n` : '') + `Adjunto podrá encontrar la factura solicitada:\n` + `${invoiceUrl}\n\n` + `Factura N° ${consecStr} · Total: ${new Intl.NumberFormat('es-CR', {
        style: 'currency',
        currency: 'CRC',
        minimumFractionDigits: 0
      }).format(payment.amount)}\n\n` + `Att: ${billingInfo.business_name}`;
      const subject = `Factura ${consecStr} — ${billingInfo.business_name}`;
      const mailtoUrl = `mailto:${encodeURIComponent(order.invoice_email || '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailBody)}`;
      window.location.href = mailtoUrl;
      showToast('success', 'Factura generada y cliente de correo abierto.');
      refetch();
    } catch (err: any) {
      console.error('[NOIZA] Error sending invoice:', err);
      showToast('error', err.message || 'Error inesperado al enviar la factura.');
    } finally {
      setProcessingId(null);
    }
  };
  const handlePrint = async (order: InvoiceRequestOrder) => {
    if (!billingInfo || !order.invoice_payment_id) return;
    // We need the full payment record to print.
    // For simplicity, we can fetch it here or just show a message.
    // Since we don't have the full payment record in the order object, let's fetch it.
    try {
      const {
        supabase
      } = await import('../../lib/supabase');
      const {
        data: payment,
        error
      } = await supabase.from('payments').select('*').eq('id', order.invoice_payment_id).single();
      if (error || !payment) throw new Error('No se encontró el pago.');
      openPrintableInvoice({
        info: billingInfo,
        payment: payment as PaymentRecord,
        tableLabel: `Orden #${order.order_number.split('-').pop()}`
      });
    } catch (err) {
      showToast('error', 'Error al obtener datos para imprimir.');
    }
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3432">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-3433">
          <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-3434" />
        </div>
      </AdminLayout>;
  }
  const displayRequests = activeTab === 'pending' ? pendingRequests : sentRequests;
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-3435">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-3436">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-3437">
          <div data-id="element-3438">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-3439">
              <FileText className="w-8 h-8 text-amber-500" data-id="element-3440" />
              Facturas Solicitadas
              {pendingRequests.length > 0 && <span className="bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-3441">
                  {pendingRequests.length} pendientes
                </span>}
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-3442">
              Gestiona las facturas solicitadas por clientes desde el menú
              digital.
            </p>
          </div>
        </div>

        {/* Billing Info Warning */}
        {!infoLoading && !billingInfo && <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 flex gap-3" data-id="element-3443">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-3444" />
            <div data-id="element-3445">
              <h3 className="text-sm font-bold text-red-400" data-id="element-3446">
                Falta información legal
              </h3>
              <p className="text-xs text-red-400/70 mt-1" data-id="element-3447">
                No se han configurado los datos legales de este restaurante. No
                podrás generar facturas hasta que se configuren.
              </p>
            </div>
          </div>}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10 pb-px" data-id="element-3448">
          <button onClick={() => setActiveTab('pending')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'pending' ? 'border-amber-500 text-amber-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-3449">
            Pendientes ({pendingRequests.length})
          </button>
          <button onClick={() => setActiveTab('sent')} className={cn('px-4 py-2.5 text-sm font-bold border-b-2 transition-colors', activeTab === 'sent' ? 'border-emerald-500 text-emerald-500' : 'border-transparent text-white/50 hover:text-white/80')} data-id="element-3450">
            Enviadas hoy ({sentRequests.length})
          </button>
        </div>

        {/* List */}
        {requestsLoading ? <div className="flex justify-center py-20" data-id="element-3451">
            <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-3452" />
          </div> : displayRequests.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-3453">
            {activeTab === 'pending' ? <>
                <CheckCircle2 className="w-16 h-16 text-emerald-500/20 mb-4" data-id="element-3454" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-3455">
                  🎉 Sin facturas pendientes
                </h3>
                <p className="text-sm" data-id="element-3456">Todo está al día.</p>
              </> : <>
                <FileText className="w-16 h-16 text-white/10 mb-4" data-id="element-3457" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-3458">
                  No hay facturas enviadas hoy
                </h3>
              </>}
          </div> : <div className="grid gap-4" data-id="element-3459">
            {displayRequests.map(order => {
          const isProcessing = processingId === order.id;
          const shortOrderNum = order.order_number.split('-').pop();
          const timeAgo = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          return <div key={order.id} className="bg-[#141414] border border-white/5 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row gap-4 md:items-center justify-between transition-all hover:border-white/10" data-id="element-3460">
                  <div className="flex-1 min-w-0" data-id="element-3461">
                    <div className="flex items-center gap-3 mb-2" data-id="element-3462">
                      <span className="text-lg font-black text-white tabular-nums" data-id="element-3463">
                        #{shortOrderNum}
                      </span>
                      <span className="text-sm font-bold text-[#F4D35E]" data-id="element-3464">
                        {formatCurrency(order.total)}
                      </span>
                      <span className="text-xs font-medium text-white/40 flex items-center gap-1" data-id="element-3465">
                        <Clock className="w-3 h-3" data-id="element-3466" />
                        {timeAgo < 60 ? `Hace ${timeAgo}m` : `Hace ${Math.floor(timeAgo / 60)}h`}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm" data-id="element-3467">
                      <div data-id="element-3468">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-3469">
                          Datos de Facturación
                        </p>
                        <p className="font-bold text-white truncate" data-id="element-3470">
                          {order.invoice_customer_name}
                        </p>
                        {order.invoice_legal_id && <p className="text-white/60 text-xs mt-0.5" data-id="element-3471">
                            ID: {order.invoice_legal_id}
                          </p>}
                        <p className="text-white/60 text-xs mt-0.5 flex items-center gap-1" data-id="element-3472">
                          <Mail className="w-3 h-3" data-id="element-3473" /> {order.invoice_email}
                        </p>
                      </div>

                      <div data-id="element-3474">
                        <p className="text-white/40 text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-3475">
                          Datos de la Orden
                        </p>
                        <p className="text-white/80 truncate" data-id="element-3476">
                          {order.customer_name}
                        </p>
                        <p className="text-white/60 text-xs mt-0.5 capitalize" data-id="element-3477">
                          {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Local'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row md:flex-col gap-2 shrink-0 border-t border-white/5 pt-4 md:border-t-0 md:pt-0" data-id="element-3478">
                    {activeTab === 'pending' ? <Button onClick={() => handleSendInvoice(order)} disabled={isProcessing || !billingInfo} className="flex-1 md:flex-none bg-amber-500 hover:bg-amber-400 text-black font-bold" data-id="element-3479">
                        {isProcessing ? <span className="flex items-center justify-center gap-2" data-id="element-3480">
                            <Loader2 className="w-4 h-4 animate-spin" data-id="element-3481" />{' '}
                            Procesando...
                          </span> : <span className="flex items-center justify-center gap-2" data-id="element-3482">
                            <Mail className="w-4 h-4" data-id="element-3483" /> Generar y enviar
                          </span>}
                      </Button> : <>
                        <div className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-xl text-sm font-bold" data-id="element-3484">
                          <CheckCircle2 className="w-4 h-4" data-id="element-3485" /> Enviada
                        </div>
                        <Button variant="outline" onClick={() => handlePrint(order)} className="flex-1 md:flex-none flex items-center justify-center gap-2 border-white/10 text-white/70 hover:bg-white/5" data-id="element-3486">
                          <Printer className="w-4 h-4" data-id="element-3487" /> Imprimir
                        </Button>
                      </>}
                  </div>
                </div>;
        })}
          </div>}

        {/* Toast */}
        {toast && <div className="fixed bottom-6 right-6 z-50 pointer-events-none" data-id="element-3488">
            <div className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-3489">
              <span className="flex-1" data-id="element-3490">{toast.message}</span>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}